import React, { useState, useEffect } from 'react'
import { makeStyles, Tooltip, Typography } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { useClientOptions } from '@paintscout/ui-business'
import classNames from 'classnames'
import type { CalendarEvent } from 'paintscout'
import { getCrews } from '@paintscout/util/builder'
import { CREW_COLORS } from '@paintscout/util'
import ProductionEventPopper from '../ProductionEventPopper'

const useStyles = makeStyles<Theme, { isHovered: boolean; color: string }>((theme) => ({
  root: {
    backgroundColor: ({ color, isHovered }) => (isHovered ? `${color}40` : `${color}20`),
    borderRadius: theme.borderRadius.md,
    borderLeft: ({ color }) => `4px solid ${color || theme.paintscout.palette.primary}`,
    cursor: 'pointer',
    boxShadow: theme.shadows[1],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: '24px',
    padding: theme.spacing(0, 1),
    transition: 'background-color 0.1s ease-in-out',
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        backgroundColor: ({ color }) => `${color}40`,
        transition: 'background-color 0.1s ease-in-out'
      }
    }
  },
  content: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    overflow: 'hidden'
  },
  label: {
    fontSize: theme.typography.pxToRem(12),
    lineHeight: 1,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  check: {
    color: ({ color }) => color
  },
  crewDotWrapper: {
    display: 'flex',
    alignItems: 'center',
    lineHeight: 0
  },
  crewDot: {
    width: '10px',
    height: '10px',
    display: 'block',
    borderRadius: '50%',
    flexShrink: 0
  }
}))

interface MonthEventProps {
  event: CalendarEvent
}

export function MonthEvent(props: MonthEventProps) {
  const { event } = props
  const [isHovered, setIsHovered] = useState(false)
  const { options } = useClientOptions()
  const crewOptions = getCrews({ options, includeTrashed: true })
  const eventCrew = event?.crew ? crewOptions.find((crew) => crew._id === event.crew) : null
  const classes = useStyles({ isHovered, color: eventCrew?.color || CREW_COLORS[0][0] })

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  // Create a BroadcastChannel
  const hoverChannel = new BroadcastChannel('hover-events')

  const handleMouseEnter = () => {
    hoverChannel.postMessage({ eventId: event.id }) // Broadcast the hover event
  }

  const handleMouseLeave = () => {
    hoverChannel.postMessage({ eventId: null }) // Broadcast that no event is hovered
  }

  useEffect(() => {
    // Listen for hover events
    const handleHoverEvent = (message: MessageEvent) => {
      if (message.data.eventId === event.id) {
        setIsHovered(true)
      } else {
        setIsHovered(false)
      }
    }

    hoverChannel.addEventListener('message', handleHoverEvent)

    return () => {
      hoverChannel.removeEventListener('message', handleHoverEvent)
      hoverChannel.close() // Clean up the channel
    }
  }, [event.id, hoverChannel])

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget)
  }

  return (
    <>
      <div
        className={classes.root}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className={classes.content}>
          {eventCrew && (
            <Tooltip content={eventCrew?.name || null} hideIcon>
              <div className={classes.crewDotWrapper}>
                <div
                  data-testid="month-crew-dot"
                  className={classes.crewDot}
                  style={{
                    backgroundColor: eventCrew?.color || CREW_COLORS[0][0]
                  }}
                />
              </div>
            </Tooltip>
          )}
          <Typography className={classes.label}>{event?.deal?.name}</Typography>
        </div>
        {event.progress === 100 && <i className={classNames('fa-regular fa-circle-check fa-xs', classes.check)} />}
      </div>
      <ProductionEventPopper event={event} onClose={() => setAnchorEl(null)} anchorEl={anchorEl} />
    </>
  )
}
